import { FC, useCallback, useState } from 'react';

import StepHeader from '@components/quiz/shared/StepHeader';
import CtaButton from '@components/quiz/shared/CtaButton';
import ImportReviewStep from '@components/quiz/shared/ImportReviewStep';
import {
  PlayerPickerProvider,
  usePlayerPicker
} from '@components/manual-team-entry/player-picker-provider';
import PlayerRow, {
  PITCH_XI_ROWS,
  PITCH_BENCH_ROW
} from '@components/manual-team-entry/player-row';
import PitchShell from '@components/manual-team-entry/pitch-shell';
import {
  TEMPLATE_LOAD_ERROR_MESSAGE,
  TEMPLATE_PLAYER_OPTA_IDS
} from '@components/manual-team-entry/template-players';
import { getTeamTotalValue } from '@components/manual-team-entry/team-helpers';
import type { TeamMember } from '@components/manual-team-entry/types';
import { fetchPlayers } from '@api-service/players';
import { useGytr } from '@components/quiz/GytrProvider';
import type { SanityGytrManualEntryStepBlock } from '@lib/sanity/types';
import {
  assessTeamBudget,
  roundToDisplayedTenth,
  TEAM_BUDGET_MILLIONS
} from '@lib/quiz/team-budget';
import type { GytrTeamMember } from 'types/gytr';

type GytrManualEntryStepProps = SanityGytrManualEntryStepBlock & {
  onContinue: () => void;
  onBack: () => void;
};

const EMPTY_TEAM: TeamMember[] = Array.from({ length: 15 }, (_, index) => ({
  playerId: index,
  player: null
}));

// Bridge the builder's full Player objects to the team-rating payload shape.
// Predicted points are omitted: the server rates the squad and picks the
// captain from its own predictions, so the client no longer needs them.
const toGytrTeam = (team: TeamMember[]): GytrTeamMember[] =>
  team.map(({ playerId, player }) => ({
    playerId,
    player: player
      ? {
          id: player.id,
          displayName: player.displayName,
          position: player.position,
          team: { shortName: player.team.shortName },
          price: player.price,
          selectedByPercent: player.ownership,
          fixtures: player.fixtures.map((f) => ({
            gameweek: f.gameweek,
            opponent: { shortName: f.opponent.shortName },
            isHome: f.isHome
          }))
        }
      : null
  }));

const GytrManualEntryInner: FC<GytrManualEntryStepProps> = ({
  header,
  budgetLabel,
  continueButtonText,
  backText,
  onContinue,
  onBack
}) => {
  const { actions } = useGytr();
  const { closeSlot } = usePlayerPicker();
  const [team, setTeam] = useState<TeamMember[]>(EMPTY_TEAM);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const isTeamComplete = team.every(({ player }) => Boolean(player));

  const totalTeamValue = roundToDisplayedTenth(getTeamTotalValue(team));
  const { isOverBudget, isOverCeiling } = assessTeamBudget(totalTeamValue);

  const handleDelete = useCallback(
    (playerId: number) => {
      setTeam((previous) => {
        const next = [...previous];
        next[playerId] = { playerId, player: null };
        return next;
      });
      closeSlot();
    },
    [closeSlot]
  );

  const handleLoadTemplate = useCallback(async () => {
    setIsLoadingTemplate(true);
    setTemplateError(null);
    try {
      const response = await fetchPlayers({
        optaIds: TEMPLATE_PLAYER_OPTA_IDS.join(','),
        limit: 15,
        exclude: 'predictions'
      });
      // Refuse a partial squad: a stale opta id would otherwise render as a
      // silent empty slot and leave the continue button disabled.
      if (response.data.length !== TEMPLATE_PLAYER_OPTA_IDS.length) {
        setTemplateError(TEMPLATE_LOAD_ERROR_MESSAGE);
        return;
      }
      const byOptaId = new Map(
        response.data.map((p) => [p.externalIds.optaUuid, p])
      );
      setTeam(
        TEMPLATE_PLAYER_OPTA_IDS.map((optaId, index) => ({
          playerId: index,
          player: byOptaId.get(optaId) ?? null
        }))
      );
    } catch {
      setTemplateError(TEMPLATE_LOAD_ERROR_MESSAGE);
    } finally {
      setIsLoadingTemplate(false);
    }
  }, []);

  const handleContinue = useCallback(() => {
    if (!isTeamComplete || isOverCeiling) return;
    actions.setTeamManually(toGytrTeam(team));
    onContinue();
  }, [actions, team, isTeamComplete, isOverCeiling, onContinue]);

  return (
    <section
      className="flex flex-col items-center px-4 pb-16 pt-6"
      onClick={() => closeSlot()}
    >
      <StepHeader
        eyebrow={header?.eyebrow}
        accentHeading={header?.heading}
        subtext={header?.subtext}
      />

      <div className="sticky top-14 z-20 mt-4 flex flex-col items-center rounded-lg bg-white px-4 py-2 shadow-100">
        <p className="mb-2 font-heading text-sm font-bold uppercase tracking-widest text-primary-500">
          {budgetLabel || 'In the bank'}
        </p>
        <div className="flex items-center font-body font-bold">
          <span
            className={isOverBudget ? 'text-danger-600' : 'text-success-600'}
          >
            £{totalTeamValue.toFixed(1)}
          </span>
          <span className="ml-1 text-neutral-600">
            / £{TEAM_BUDGET_MILLIONS}m
          </span>
        </div>
      </div>

      <div className="mt-4 w-full max-w-sm">
        <CtaButton
          variant="secondary"
          onClick={handleLoadTemplate}
          disabled={isLoadingTemplate}
        >
          {isLoadingTemplate ? 'Loading…' : 'Use template team'}
        </CtaButton>
        {templateError && (
          <p className="mt-2 text-center font-body text-sm text-danger-600">
            {templateError}
          </p>
        )}
      </div>

      <PitchShell
        xi={PITCH_XI_ROWS.map((row) => (
          <PlayerRow
            key={`gytr-pitch-row-${row.positionId}`}
            row={row}
            team={team}
            setTeam={setTeam}
            onDelete={handleDelete}
          />
        ))}
        bench={
          <PlayerRow
            row={PITCH_BENCH_ROW}
            team={team}
            setTeam={setTeam}
            onDelete={handleDelete}
          />
        }
      />

      <div className="mt-6 w-full max-w-sm space-y-3">
        <CtaButton
          onClick={handleContinue}
          disabled={!isTeamComplete || isOverCeiling}
          dataCy="gytr-manual-continue"
        >
          {continueButtonText ?? 'Continue to my rating'}
        </CtaButton>
        <button
          type="button"
          onClick={onBack}
          className="block w-full text-center font-body text-sm font-bold text-primary-500 transition-colors hover:text-primary-600"
        >
          {backText ?? 'Back'}
        </button>
      </div>
    </section>
  );
};

/**
 * Step 1b: the build-manually pitch (spec 5.6). Reuses the interactive
 * builder pieces (picker provider, pitch shell, player rows) so it stays
 * visually identical to the onboarding builder, then bridges the squad into
 * the GYTR context on continue.
 *
 * Doubles as the screenshot review step (FFH-2738). A read the rules refuse
 * lands here with whatever fitted already placed, so correcting it is the same
 * pitch and the same picker as building one by hand. It reuses this block
 * rather than adding its own so the funnel's progress pips, hash navigation and
 * refresh guard all keep working untouched.
 */
const GytrManualEntryStep: FC<GytrManualEntryStepProps> = (props) => {
  const { state, actions } = useGytr();
  const { onContinue, onBack } = props;

  const handleReviewConfirm = useCallback(
    (team: TeamMember[]) => {
      actions.setTeamFromReview(toGytrTeam(team));
      onContinue();
    },
    [actions, onContinue]
  );

  const handleReviewBack = useCallback(() => {
    actions.setIsReviewingScreenshot(false);
    onBack();
  }, [actions, onBack]);

  if (state.isReviewingScreenshot && state.screenshotMatches) {
    return (
      <ImportReviewStep
        matches={state.screenshotMatches}
        continueLabel={props.continueButtonText ?? 'Continue to my rating'}
        backLabel="Start again with a different screenshot"
        // The reason the rating refused this squad, when that is what sent
        // them here. Shown on the pitch rather than on a screen of its own.
        submitError={state.calculationFailure?.message ?? null}
        onConfirm={handleReviewConfirm}
        onBack={handleReviewBack}
      />
    );
  }

  return (
    <PlayerPickerProvider>
      <GytrManualEntryInner {...props} />
    </PlayerPickerProvider>
  );
};

export default GytrManualEntryStep;
